import { useCallback, useEffect, useLayoutEffect, useRef } from "react";

import { offset } from "@floating-ui/dom";
import { v4 } from "uuid";

import { useFloatingWindowsActions } from "src/base-components/FloatingWindow/hooks";
import { QuerySuggestionType } from "src/copilot/QuerySuggestion";

export const FEATURE_QUERY_COPILOT_WINDOW_ID = "feature-query-copilot";

/**
 * Hook to manage copilot for feature queries.
 * Each instance maintains its own conversation using a ref.
 * The floating window is automatically closed when the hook unmounts.
 */
export const useFeatureQueryCopilot = () => {
  const { open: openWindow, close: closeWindow } = useFloatingWindowsActions();
  const copilotTargetRef = useRef<HTMLDivElement | null>(null);
  const conversationIdRef = useRef<string>(v4());

  const resetConversation = useCallback(() => {
    conversationIdRef.current = v4();
  }, []);

  const openCopilot = useCallback(
    (options?: {
      prompt?: string;
      getCurrentQuery: () => string;
      getLastQueryErrorMessage: () => string;
      onApplyQuery?: (suggestion: QuerySuggestionType) => void;
    }) => {
      const windowData = {
        conversationId: conversationIdRef.current,
        resetConversationId: resetConversation,
        initialPrompt: options?.prompt,
        getCurrentQuery: options?.getCurrentQuery,
        getLastQueryErrorMessage: options?.getLastQueryErrorMessage,
        onApplyQuery: options?.onApplyQuery,
      };
      openWindow(
        {
          id: FEATURE_QUERY_COPILOT_WINDOW_ID,
          type: "feature-query-copilot",
          target: copilotTargetRef,
          positionOptions: {
            placement: "right-start",
            middleware: [offset({ mainAxis: 10, alignmentAxis: 10 })],
          },
        },
        windowData,
      );
    },
    [openWindow, resetConversation],
  );

  useLayoutEffect(() => {
    // Find the feature query form container or a suitable reference element
    const featureQueryContainer = document.querySelector(
      "[data-loc=catalogue-wrapper]",
    );
    copilotTargetRef.current = featureQueryContainer as HTMLDivElement;
  }, []);

  // Close the floating window when the hook unmounts
  useEffect(() => {
    return () => {
      closeWindow(FEATURE_QUERY_COPILOT_WINDOW_ID);
    };
  }, [closeWindow]);

  return {
    openFeatureQueryCopilot: openCopilot,
    resetConversation,
  };
};
